/**
 * useAikoTarget — resolve an `<AikoAnchor id>` into a live element + state.
 *
 *   const { element, exists, visible, disabled } = useAikoTarget("wizard.cover-button");
 *
 * Returns a snapshot that re-renders when the matching element appears,
 * disappears, becomes hidden, or gains/loses a `disabled` attribute. Both
 * `<AikoProvider>`'s hint resolver and consumer code can use this hook to
 * make the same decisions about a target — visible vs hidden vs disabled vs
 * missing — without each caller wiring its own `MutationObserver`.
 *
 * Visibility is checked via `offsetParent` (cheap, picks up `display:none`
 * on any ancestor) plus an explicit `hidden` attribute check. Position-fixed
 * elements (which have `null` offsetParent in spec) are treated as visible
 * unless their computed style says otherwise.
 *
 * Disabled is checked via the native `disabled` property (buttons, inputs,
 * fieldsets, selects, textareas) and via `aria-disabled="true"` for
 * non-form-control elements.
 *
 * SSR-safe: returns the empty snapshot on the server and resolves on the
 * first client effect.
 */
import * as React from "react";

export interface AikoTargetSnapshot {
  /** The resolved DOM element, or `null` when not in DOM. */
  element: HTMLElement | null;
  /** True iff an element with `data-aiko-anchor={id}` exists in the DOM. */
  exists: boolean;
  /** True iff the element exists AND is visible (not `display:none` / `hidden`). */
  visible: boolean;
  /** True iff the element exists AND is disabled (`disabled` or `aria-disabled="true"`). */
  disabled: boolean;
}

const EMPTY: AikoTargetSnapshot = {
  element: null,
  exists: false,
  visible: false,
  disabled: false,
};

export function useAikoTarget(id: string | null | undefined): AikoTargetSnapshot {
  const [snapshot, setSnapshot] = React.useState<AikoTargetSnapshot>(EMPTY);

  React.useEffect(() => {
    if (!id || typeof document === "undefined") {
      setSnapshot(EMPTY);
      return;
    }

    const selector = `[data-aiko-anchor="${escapeAttr(id)}"]`;

    const evaluate = () => {
      const el = document.querySelector<HTMLElement>(selector);
      if (!el) {
        setSnapshot((prev) => (prev.exists ? EMPTY : prev));
        return;
      }
      const next: AikoTargetSnapshot = {
        element: el,
        exists: true,
        visible: isVisible(el),
        disabled: isDisabled(el),
      };
      setSnapshot((prev) => (snapshotsEqual(prev, next) ? prev : next));
    };

    evaluate();

    // One observer per anchor id. Tree-wide because the target may mount or
    // move anywhere; the attribute filter keeps churn low even on busy apps.
    const observer = new MutationObserver(evaluate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "data-aiko-anchor",
        "disabled",
        "aria-disabled",
        "hidden",
        "style",
        "class",
      ],
    });

    return () => observer.disconnect();
  }, [id]);

  return snapshot;
}

function isVisible(el: HTMLElement): boolean {
  if (typeof window === "undefined") return false;
  // Walk the ancestor chain checking display / visibility / `hidden` at each
  // level. Avoids `offsetParent` (which is null in jsdom regardless of actual
  // visibility) and handles the common cases that matter for the hint
  // resolver: display:none on self or any ancestor, visibility:hidden, the
  // HTML `hidden` attribute. Off-screen positioning isn't checked — the
  // element is still "in the document" and a whisper can still anchor to it.
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    if (node.hidden) return false;
    const cs = window.getComputedStyle(node);
    if (cs.display === "none") return false;
    if (cs.visibility === "hidden" || cs.visibility === "collapse") return false;
    node = node.parentElement;
  }
  return true;
}

function isDisabled(el: HTMLElement): boolean {
  if ((el as HTMLButtonElement).disabled === true) return true;
  if (el.getAttribute("aria-disabled") === "true") return true;
  return false;
}

function snapshotsEqual(a: AikoTargetSnapshot, b: AikoTargetSnapshot): boolean {
  return (
    a.element === b.element &&
    a.exists === b.exists &&
    a.visible === b.visible &&
    a.disabled === b.disabled
  );
}

function escapeAttr(value: string): string {
  return value.replace(/["\\]/g, "\\$&");
}
